/* eslint-disable max-lines-per-function */
import { compact, isEmpty, noop } from 'lodash';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import {
   MedicalServiceWithPractitionersSimple,
   MedicalServiceWithPractitionersSimpleQuery,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { useLazyLoadedDataConfigurationByCqrsQuery } from '@dreryk/drerykweb-main-frontend';
import { Ordering } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   HorizontalAlign,
   JustifyContent,
   Link,
   RowDetailsExpander,
   RowDetailsRenderer,
   Table,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';
import { TextPattern } from '@dreryk/watermelon';

import { medicalServiceTranslationKeys } from '../../translations';
import { MedicalServiceCreateModalOpener } from '../create/MedicalServiceCreateModalOpener';

import { MedicalServiceOptionsCell } from './cells/MedicalServiceOptionsCell';
import { MedicalServicePriceCell } from './cells/MedicalServicePriceCell';
import { PractitionerRowDetails } from './rowDetails/PractitionerRowDetails';

const StyledEmptyStatePlaceholder = styled(EmptyStatePlaceholder)`
   width: 537px;
`;

StyledEmptyStatePlaceholder.displayName = 'StyledEmptyStatePlaceholder';

type MedicalServiceTablePlaceholderProps = {
   readonly noSearchResults: boolean;
};

const MedicalServiceTablePlaceholder = (props: MedicalServiceTablePlaceholderProps): JSX.Element => {
   if (props.noSearchResults) {
      return (
         <StyledEmptyStatePlaceholder
            testId={'noSearchResults'}
            heading={medicalServiceTranslationKeys.table.placeholder.noSearchResults.heading}
            fullLength
         />
      );
   }

   return (
      <StyledEmptyStatePlaceholder
         testId={'noMedicalService'}
         heading={medicalServiceTranslationKeys.table.placeholder.noMedicalService.heading}
         subheading={medicalServiceTranslationKeys.table.placeholder.noMedicalService.subheading}
         fullLength
      >
         <MedicalServiceCreateModalOpener>
            {({ openModal }) => (
               <Link
                  testId={'medicalServiceCreate'}
                  label={medicalServiceTranslationKeys.table.placeholder.noMedicalService.link}
                  onClick={openModal}
               />
            )}
         </MedicalServiceCreateModalOpener>
      </StyledEmptyStatePlaceholder>
   );
};

const DEFAULT_ORDERINGS: Ordering<MedicalServiceWithPractitionersSimple>[] = [
   {
      descending: false,
      property: 'name',
   },
];

type MedicalServiceTableProps = {
   readonly searchText: string | null;
   readonly hasMorePractitioners: boolean;
};

export const MedicalServiceTable = (props: MedicalServiceTableProps): JSX.Element => {
   const dataConfiguration = useLazyLoadedDataConfigurationByCqrsQuery<MedicalServiceWithPractitionersSimple>(() => {
      const query = new MedicalServiceWithPractitionersSimpleQuery();
      if (props.searchText) {
         query.name = TextPattern.anywhereIgnoreCase(props.searchText);
      }
      return query;
   }, DEFAULT_ORDERINGS);

   const columns = useMemo((): TableColumnConfiguration<MedicalServiceWithPractitionersSimple>[] => {
      const columnsWidth = createDynamicColumnsWidth({
         name: '100%',
         price: '190px',
         practitioners: props.hasMorePractitioners ? '90px' : '0px',
         options: '64px',
         expander: props.hasMorePractitioners ? '50px' : '0px',
      });

      return compact([
         {
            name: 'name',
            accessor: 'name',
            label: medicalServiceTranslationKeys.table.headers.name,
            width: columnsWidth.name,
         },
         {
            name: 'price',
            label: medicalServiceTranslationKeys.table.headers.price,
            width: columnsWidth.price,
            textAlign: HorizontalAlign.RIGHT,
            justifyContent: JustifyContent.END,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<MedicalServiceWithPractitionersSimple>): JSX.Element => (
               <MedicalServicePriceCell
                  medicalServiceDefaultPrice={bean.defaultPrice}
                  medicalServicePractitioners={bean.practitioners}
               />
            ),
            sortingDisabled: true,
         },
         props.hasMorePractitioners && {
            name: 'practitioners',
            label: medicalServiceTranslationKeys.table.headers.practitioners,
            width: columnsWidth.practitioners,
            textAlign: HorizontalAlign.RIGHT,
            justifyContent: JustifyContent.END,
            accessor: (bean) => bean.practitioners.length,
            sortingDisabled: true,
         },
         {
            name: 'options',
            label: '',
            width: columnsWidth.options,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<MedicalServiceWithPractitionersSimple>): JSX.Element => (
               <MedicalServiceOptionsCell medicalServiceId={bean.id} medicalServiceName={bean.name} />
            ),
            sortingDisabled: true,
         },
         props.hasMorePractitioners && {
            name: 'expander',
            label: '',
            width: columnsWidth.expander,
            accessor: noop,
            textAlign: HorizontalAlign.CENTER,
            sortingDisabled: true,

            renderer: (renderProps: ValueRendererProps<MedicalServiceWithPractitionersSimple>): JSX.Element => {
               return (
                  <RowDetailsExpander
                     {...renderProps}
                     testId={'expander'}
                     disabled={isEmpty(renderProps.bean.practitioners)}
                  />
               );
            },
         },
      ]);
   }, [props.hasMorePractitioners]);

   const rowDetailsRenderer = useCallback<RowDetailsRenderer<MedicalServiceWithPractitionersSimple>>(({ bean }) => {
      return bean.practitioners.map(({ practitioner, individualPrice, id: practitionerMedicalServiceId }) => (
         <PractitionerRowDetails
            key={practitioner.id}
            practitionerId={practitioner.id}
            practitionerName={`${practitioner.firstName.asString()} ${practitioner.lastName.asString()}`}
            practitionerSpecialtyNames={practitioner.specialtyNames}
            individualPrice={individualPrice}
            defaultPrice={bean.defaultPrice}
            medicalService={bean}
            practitionerMedicalServiceId={practitionerMedicalServiceId}
            hasMorePractitioners={bean.practitioners.length > 1}
         />
      ));
   }, []);

   return (
      <Table
         testId={'medicalService'}
         keyExtractor={'id'}
         dataConfiguration={dataConfiguration}
         columns={columns}
         placeholder={<MedicalServiceTablePlaceholder noSearchResults={Boolean(props.searchText)} />}
         rowDetailsRenderer={rowDetailsRenderer}
         pageSize={1000}
      />
   );
};
